import React, { useState, useRef, useEffect } from 'react';
import ReactGA from 'react-ga4';
import Clarity from '@microsoft/clarity';

const AIChatAssistant = ({ user, analysisResult }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi! I\'m your AI assistant. Ask me anything about video analysis, interpretation of results, or how to use GradiAI! 🤖',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isChatOpen) {
      inputRef.current?.focus();
    }
  }, [isChatOpen]);

  // Context-aware responses based on analysis
  const getContextualResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();
    
    // Help with features
    if (lowerMessage.includes('how') && (lowerMessage.includes('use') || lowerMessage.includes('work'))) {
      return 'To analyze a video:\n1. Paste a YouTube URL\n2. Click "Analyze Video"\n3. Wait for AI analysis\n4. View ratings and export results!\n\nYou can also check the Leaderboard and compare videos.';
    }

    // Explain scores
    if (lowerMessage.includes('score') || lowerMessage.includes('rating')) {
      if (analysisResult) {
        const score = analysisResult.analysis?.gradi_score || 0;
        return `The current video has a Gradi Score of ${score}/5.0.\n\nThis score is calculated based on:\n• Content Quality\n• Delivery Style\n• Engagement\n• Educational Value\n• Technical Accuracy\n\nHigher scores indicate better overall quality!`;
      }
      return 'Gradi Scores range from 0-5:\n\n🌟 4.0-5.0: Excellent\n⭐ 3.0-3.9: Good\n✨ 2.0-2.9: Average\n💫 Below 2.0: Needs Improvement\n\nAnalyze a video to see its detailed scores!';
    }

    // Explain categories
    if (lowerMessage.includes('categor') || lowerMessage.includes('metric')) {
      return 'We analyze videos across 5 key categories:\n\n📚 Content Quality - Information accuracy & depth\n🎤 Delivery Style - Presentation & clarity\n💡 Engagement - How captivating the content is\n🎓 Educational Value - Learning effectiveness\n⚙️ Technical Accuracy - Correctness of information';
    }

    // Export options
    if (lowerMessage.includes('export') || lowerMessage.includes('download') || lowerMessage.includes('save')) {
      return 'You can export analysis results in multiple ways:\n\n📄 JSON - Download structured data\n🖼️ Image - Save as PNG screenshot\n📋 Copy - Copy to clipboard\n\nFind these options below each analysis!';
    }

    // Comparison feature
    if (lowerMessage.includes('compar')) {
      return 'To compare videos:\n1. Go to "Compare" page from navbar\n2. Add 2-3 videos\n3. View side-by-side ratings\n4. See score differences\n5. Export comparison report\n\nGreat for choosing between similar content!';
    }


    // Dashboard
    if (lowerMessage.includes('dashboard') || lowerMessage.includes('analytics') || lowerMessage.includes('history')) {
      return 'Your Personal Dashboard shows:\n\n📊 Analysis history\n📈 Usage statistics\n⭐ Favorite videos\n🔄 Recent analyses\n📉 Trend charts\n\nCheck it out from the navbar!';
    }

    // Current analysis insights
    if (analysisResult && (lowerMessage.includes('this video') || lowerMessage.includes('current') || lowerMessage.includes('result'))) {
      const analysis = analysisResult.analysis;
      const score = analysis?.gradi_score || 0;
      const title = analysisResult.title || 'this video';
      
      let response = `Analysis of "${title}":\n\n`;
      response += `⭐ Overall Score: ${score}/5.0\n\n`;
      
      if (analysis?.ratings) {
        response += 'Top strengths:\n';
        const ratings = Object.entries(analysis.ratings)
          .sort((a, b) => b[1].score - a[1].score)
          .slice(0, 2);
        ratings.forEach(([key, value]) => {
          response += `✓ ${key.replace(/_/g, ' ')}: ${value.score}/5.0\n`;
        });
      }
      
      response += '\nWant to know more about a specific category?';
      return response;
    }

    // Leaderboard
    if (lowerMessage.includes('leaderboard') || lowerMessage.includes('top')) {
      return 'The Leaderboard shows:\n\n🏆 Top 10 highest-rated videos\n📊 Real-time rankings\n🔄 Auto-updates every 30s\n🎯 Filter by language\n\nClick any video to analyze it instantly!';
    }

    // Mind map
    if (lowerMessage.includes('mind map') || lowerMessage.includes('visual')) {
      return 'Mind Maps visualize:\n\n🧠 Key concepts from video\n🔗 Topic relationships\n📊 Score visualization\n💡 Interactive exploration\n\nAvailable after analyzing a video!';
    }

    // Default helpful response
    return 'I can help you with:\n\n🎥 Analyzing videos\n📊 Understanding scores\n🔄 Comparing videos\n💾 Exporting results\n📈 Using the dashboard\n\nWhat would you like to know?';
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMsg = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    setIsTyping(true);

    // Track chat interaction
    ReactGA.event({
      category: 'AI Chat',
      action: 'Message Sent',
      label: 'User Query'
    });
    Clarity.event('ai_chat_message');

    // Simulate thinking delay
    setTimeout(() => {
      const response = getContextualResponse(inputMessage);
      const assistantMsg = {
        role: 'assistant',
        content: response,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMsg]);
      setIsTyping(false);
    }, 800);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickPrompts = [
    '❓ How do I use GradiAI?',
    '📊 Explain the scores',
    '🔄 How to compare videos?',
  ];

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
    if (!isChatOpen) {
      ReactGA.event({
        category: 'AI Chat',
        action: 'Chat Opened',
        label: 'Toggle'
      });
      Clarity.event('ai_chat_opened');
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isChatOpen && (
        <button
          onClick={toggleChat}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
            border: 'none',
            color: 'white',
            fontSize: '1.8rem',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(139, 92, 246, 0.5)',
            zIndex: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 6px 30px rgba(139, 92, 246, 0.7)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(139, 92, 246, 0.5)';
          }}
        >
          💬
        </button>
      )}

      {/* Chat Window */}
      {isChatOpen && (
        <div style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          width: '400px',
          height: '600px',
          background: 'rgba(15, 15, 35, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: '16px',
          boxShadow: '0 8px 40px rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'slideInFromRight 0.3s ease-out'
        }}>
          {/* Header */}
          <div style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', margin: 0 }}>AI Assistant 🤖</h3>
              <p style={{ fontSize: '0.85rem', margin: '0.25rem 0 0 0', opacity: 0.9 }}>
                Ask me anything!
              </p>
            </div>
            <button
              onClick={toggleChat}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                fontSize: '1.5rem',
                width: '35px',
                height: '35px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              ×
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                style={{
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '80%',
                  animation: 'fadeInUp 0.3s ease-out'
                }}
              >
                <div style={{
                  background: msg.role === 'user' 
                    ? 'linear-gradient(135deg, #8b5cf6, #ec4899)'
                    : 'rgba(55, 65, 81, 0.6)',
                  padding: '0.75rem 1rem',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '0.95rem',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {msg.content}
                </div>
                <div style={{
                  fontSize: '0.7rem',
                  color: '#9ca3af',
                  marginTop: '0.25rem',
                  textAlign: msg.role === 'user' ? 'right' : 'left'
                }}>
                  {msg.timestamp.toLocaleTimeString()}
                </div>
              </div>
            ))}

            {isTyping && (
              <div style={{
                alignSelf: 'flex-start',
                maxWidth: '80%'
              }}>
                <div style={{
                  background: 'rgba(55, 65, 81, 0.6)',
                  padding: '0.75rem 1rem',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '0.95rem',
                  display: 'flex',
                  gap: '0.5rem'
                }}>
                  <span className="typing-dot">●</span>
                  <span className="typing-dot">●</span>
                  <span className="typing-dot">●</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Prompts */}
          {messages.length === 1 && (
            <div style={{
              padding: '0 1.5rem 1rem',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem'
            }}>
              {quickPrompts.map((prompt, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setInputMessage(prompt.substring(2));
                    setTimeout(handleSendMessage, 100);
                  }}
                  style={{
                    background: 'rgba(139, 92, 246, 0.2)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    color: '#d1d5db',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'rgba(139, 92, 246, 0.3)';
                    e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.5)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                  }}
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid rgba(139, 92, 246, 0.2)',
            display: 'flex',
            gap: '0.75rem'
          }}>
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything..."
              style={{
                flex: 1,
                background: 'rgba(55, 65, 81, 0.5)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '12px',
                padding: '0.75rem 1rem',
                color: 'white',
                fontSize: '0.95rem',
                outline: 'none',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = 'rgba(139, 92, 246, 0.6)'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(139, 92, 246, 0.3)'}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim()}
              style={{
                background: inputMessage.trim() 
                  ? 'linear-gradient(135deg, #8b5cf6, #ec4899)'
                  : 'rgba(55, 65, 81, 0.5)',
                border: 'none',
                color: 'white',
                padding: '0.75rem 1.25rem',
                borderRadius: '12px',
                cursor: inputMessage.trim() ? 'pointer' : 'not-allowed',
                fontSize: '1rem',
                fontWeight: 'bold',
                transition: 'all 0.2s ease',
                opacity: inputMessage.trim() ? 1 : 0.5
              }}
              onMouseOver={(e) => {
                if (inputMessage.trim()) {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes slideInFromRight {
          from { opacity: 0; transform: translateX(50px); }
          to { opacity: 1; transform: translateX(0); }
        }

        .typing-dot {
          animation: typingDot 1.4s infinite;
          opacity: 0.3;
        }

        .typing-dot:nth-child(1) { animation-delay: 0s; }
        .typing-dot:nth-child(2) { animation-delay: 0.2s; }
        .typing-dot:nth-child(3) { animation-delay: 0.4s; }

        @keyframes typingDot {
          0%, 60%, 100% { opacity: 0.3; }
          30% { opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default AIChatAssistant;

